import type { LeadFlowRichTextContent } from "@homeinstead/schemas";
import type { LeadFlowAnswerLabels, LeadFlowAnswers } from "../types";

import { getAnswerText } from "./get-answer-text";

type RichTextBlock = LeadFlowRichTextContent[number];

type AnswerPlaceholderMarkDef = Extract<
  NonNullable<RichTextBlock["markDefs"]>[number],
  { _type: "leadFlowAnswerPlaceholder" }
>;

type TextSpan = {
  marks: string[];
  text: string;
};

function extractTextSpan(child: unknown): TextSpan | null {
  if (typeof child !== "object" || child === null || !("text" in child)) {
    return null;
  }

  const childObject = child as { marks?: string[]; text?: unknown };

  return {
    marks: childObject.marks ?? [],
    text: typeof childObject.text === "string" ? childObject.text.trim() : "",
  };
}

function isSpanVisible(
  span: TextSpan,
  hideWhenEmptyFields: Map<string, AnswerPlaceholderMarkDef["field"]>,
  answers: LeadFlowAnswers,
  answerLabels: LeadFlowAnswerLabels,
): boolean {
  const { marks, text } = span;

  if (text && marks.length === 0) {
    return true;
  }

  if (text && !marks.some((mark) => hideWhenEmptyFields.has(mark))) {
    return true;
  }

  return marks.some((mark) => {
    const field = hideWhenEmptyFields.get(mark);

    return (
      field !== undefined &&
      getAnswerText(field, answers, answerLabels) !== undefined
    );
  });
}

function isBlockHiddenByUnansweredAnswer(
  block: RichTextBlock,
  answers: LeadFlowAnswers,
  answerLabels: LeadFlowAnswerLabels,
): boolean {
  if (block._type !== "block" || !block.children) {
    return false;
  }

  const hideWhenEmptyFields = new Map(
    (block.markDefs ?? [])
      .filter(
        (markDef): markDef is AnswerPlaceholderMarkDef =>
          markDef._type === "leadFlowAnswerPlaceholder" &&
          markDef.hideWhenEmpty === true,
      )
      .map((markDef) => [markDef._key, markDef.field] as const),
  );

  if (hideWhenEmptyFields.size === 0) {
    return false;
  }

  const textSpans = block.children
    .map(extractTextSpan)
    .filter((span): span is TextSpan => span !== null);

  return !textSpans.some((span) =>
    isSpanVisible(span, hideWhenEmptyFields, answers, answerLabels),
  );
}

/**
 * Drops blocks whose only content is an unanswered hide-when-empty answer
 * placeholder. RichText already drops those blocks for office placeholders,
 * but an answer placeholder's fallback text stays in the span, so the parent
 * block would render as an empty paragraph until the question is answered.
 */
function filterHiddenAnswerBlocks(
  blocks: LeadFlowRichTextContent,
  answers: LeadFlowAnswers,
  answerLabels: LeadFlowAnswerLabels,
): LeadFlowRichTextContent {
  return blocks.filter(
    (block) => !isBlockHiddenByUnansweredAnswer(block, answers, answerLabels),
  );
}

export { filterHiddenAnswerBlocks };
